import React from "react"
import { connect } from "react-redux"
import _find from "lodash/find"
import _sortBy from "lodash/sortBy";
import _debounce from "lodash/debounce";
import { echo } from "app/components/websocketeer";

import trans from "trans"
import {
    Theme,
    Div,
    css,
    Grid,
    GridCell,
    Select,
    Spinner,
} from "@bdfx/mergado-ui-kit"

import { setRandomProduct } from "actions";

import {
    getClonedTemplate,
    getFormName,
    recalculateShapes,
    templateFromGalleryToStore
} from "shape-edit/forms/helpers";

import { getTemplateGallery, godProjectId, isGodModeOn, loadTemplateGallery, projectId } from "app/helpers/api";

import {
    setGradientsToStore,
} from "template-edit/reducers";

import { initForm, updateInputValue as change } from "app/reducers/form"

import {
    Size,
    Template,
    CollageLayoutType,
    Template as TemplateType,
    Gradient,
    AlternativeTemplate
} from "app/types/model-types";

import { ReduxList } from "app/types/app-types";
import newTemplate from "../helpers/newTemplate";
import { AxiosResponse } from "axios";
import PreviewTemplateSVG from "../containers/preview-template-svg";
import { RandomProduct } from "shape-edit/types";
import { getLocale } from "app/helpers/session";
import { selectFormValues } from "app/reducers/form";
import TemplateGalleryRender from "./template-gallery-render";
import { SupereffectType, WebSocketMessage } from "template-edit/containers/Editor";
import { getLocaleTwoLetters } from "shape-edit/helpers/locale";
import { SizeItem } from "settings/components/sizes-settings";

export type TemplateGalleryType = {
    id: number
    trans: string
    hash: string
    width: number
    height: number
    serialized_template: string
    priority: number
    groups: string
    thumb: string
}

export const TEMPLATE_GALLERY_GROUPS = [
    "generic",
    "midjourney",
    "easter_campaign",
    "discounts_and_sales",
    "black_friday",
    "gift",
    "free_delivery",
    "new",
    "seasonal",
    "availability",
    "christmas",
    "new_year_sale",
    "effects",
    "background",
    "warranty",
    "valentine",
    "size_variant",
    "viva_magenta",
]

interface Props {
    isAlt: boolean
    sizeRatio: number
    sizes: ReduxList<SizeItem>
    form: Template|AlternativeTemplate
    collageLayoutsForm: CollageLayoutType[]
    randomProduct: RandomProduct
    initForm: typeof initForm
    change: typeof change
    setGradientsToStore: typeof setGradientsToStore
    setRandomProduct: typeof setRandomProduct
    userId: number
    templates: ReduxList<TemplateType>
}

interface State {
    templates: TemplateGalleryType[]
    oldSizeBeforeNewFormat: Size
    newFormat: Size
    isLoading: boolean
    isLoadingGallery: boolean
    filterGroup: string
}

class TemplateGalleryModal extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            templates: [],
            newFormat: {
                id: "new",
                project_id: null,
                width: 1080,
                height: 1080,
                name: trans('new'),
                templates: [],
            },
            oldSizeBeforeNewFormat: null,
            isLoading: false,
            isLoadingGallery: false,
            filterGroup: null,
        }
    }

    handleSelectCustomTemplate = (template: Template) => {
        const { form, isAlt } = this.props

        const formName = getFormName(isAlt)
        const importedTemplateSize = { ...template.size }

        const currentFormSize = _find(this.props.sizes.data, (size: SizeItem) => {
            return size?.id === form.size_id
        })

        const updatedTemplateForm: Template = {
            ...template,
            id: form.id,
            size: currentFormSize ? currentFormSize : form.size,
            size_id: currentFormSize ? currentFormSize.id : form.size_id,
        }

        if (template.size_id !== form.size_id) {
            this.props.initForm(formName, recalculateShapes(importedTemplateSize, form.size, updatedTemplateForm))
            this.setState({ oldSizeBeforeNewFormat: importedTemplateSize })
        } else {
            this.props.initForm(formName, updatedTemplateForm)
        }

    }

    handleSelectTemplate = (id?: number) => {
        const { form, isAlt, sizes } = this.props
        const formName = getFormName(isAlt)


        if (id) {
            this.setState({ isLoading: true })
            loadTemplateGallery(id)
                .then((result: AxiosResponse<{ templateGallery: TemplateGalleryType, gradients: Gradient[] }>) => {
                    this.props.setGradientsToStore(result.data.gradients)
                    templateFromGalleryToStore(result.data.templateGallery)
                })
                .catch(e => {
                    if (e.response) {
                        console.error('error', e.response)
                    } else {
                        console.error('error', e)
                    }
                })
                .finally(() => {
                    this.setState({ isLoading: false })
                })
        } else {
            const updatedTemplateForm: Template = {
                ...newTemplate(),
                id: form.id,
                project_id: projectId,
                name: form.name,
                queries: form.queries,
                priority: form.priority,
                active: form.active,
                active_from: form.active_from,
                active_to: form.active_to,
                size: form.size,
                size_id: form.size_id,
            }
            this.props.initForm(formName, updatedTemplateForm)
        }

        this.hideOnClick()
    }

    hideOnClick = () => {
        const element = document.getElementById('close-button')
        if (element) {
            element.click()
        }
    }

    updateData = (filterGroup: string) => {
        const { sizeRatio, randomProduct } = this.props

        if ((filterGroup !== "custom") && (this.props.sizeRatio === 1 || this.props.sizeRatio === 1200 / 628)) {
            this.setState({ isLoadingGallery: true })
            console.debug('updating gallery')
            getTemplateGallery(sizeRatio, filterGroup, null, randomProduct?.image_id || null)
                .then((response: AxiosResponse<TemplateGalleryType[]>) => {
                    this.setState({ templates: response.data })
                })
                .catch(error => {
                    console.error('cannot load template gallery', error)
                })
                .finally(() => this.setState({ isLoadingGallery: false }))
        }
    }

    updateDataDebounced = _debounce(() => {
        const { filterGroup } = this.state
        this.updateData(filterGroup)
    }, 500)

    componentDidMount(): void {
        this.updateData(null)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps && this.props.sizeRatio && prevProps.sizeRatio !== this.props.sizeRatio) {
            this.updateDataDebounced()
        }
    }

    render() {
        const { templates, isLoading, filterGroup, isLoadingGallery } = this.state
        const { sizeRatio, form, randomProduct, isAlt } = this.props

        const templateGalleryOptions = TEMPLATE_GALLERY_GROUPS.map(name => {
            return { value: name, label: trans(`group_${name}`) }
        })

        const locales = getLocaleTwoLetters(getLocale())

        const sortedTemplateGalleryOptions = templateGalleryOptions.sort(function (a, b) {
            return new Intl.Collator(locales, { caseFirst: 'upper' }).compare(a.label, b.label);
        })

        return <div className={isLoading ? 'muk-helper-loading' : ''}>
            <div style={{ marginBottom: '20px' }}>
                <Select
                    placeholder={trans('group_all_templates')}
                    value={filterGroup}
                    options={[
                        { value: null, label: trans(`group_all_templates`) },
                        ...sortedTemplateGalleryOptions,
                        { value: 'custom', label: trans(`group_custom`) },
                    ]}
                    onChange={(value) => {
                        this.setState({ filterGroup: value })
                        this.updateData(value)
                    }}
                    multiple={false}
                    closeOnChange={true}
                />
            </div>

            <ContentGrid cols={"1fr 1fr 1fr"}>
                <GridCell key={'empty'}>
                    <div className={'template-thumbnail'} onClick={() => this.handleSelectTemplate()}>
                        <Div display="inline-block" width="100%" padding="49% 0" background="#eee">
                            {trans('blank_template')}
                        </Div>
                    </div>
                </GridCell>

                {isLoadingGallery &&
                    <GridCell>
                        <Spinner type="bubbles" />
                    </GridCell>
                }

                {filterGroup !== 'custom' ?
                    <TemplateGalleryRender
                        isAlt={isAlt}
                        templates={templates}
                        handleSelectTemplate={this.handleSelectTemplate}
                        sizeRatio={sizeRatio}
                        randomProduct={randomProduct}
                    /> :
                    <React.Fragment>
                        {this.props.templates.isLoaded && this.props.templates.data.filter(template =>
                            Math.round(10 * template.size.width / template.size.height) === Math.round(10 * sizeRatio)
                        ).map(template => {
                            const clonedTemplate = getClonedTemplate(template, form.id)
                            const collageForTemplate = clonedTemplate.is_collage ? _find(this.props.collageLayoutsForm, (collageLayout: CollageLayoutType) => {
                                return collageLayout.id === clonedTemplate.collage_layout_id
                            }) : null

                            const updatedTemplateForm: Template = {
                                ...clonedTemplate,
                                id: form.id,
                                name: form.name,
                                queries: form.queries,
                                priority: form.priority,
                                active: form.active,
                                active_from: form.active_from,
                                active_to: form.active_to,
                                collage_layout: collageForTemplate,
                            }

                            const galleryFromTemplate: TemplateGalleryType = {
                                id: null,
                                trans: clonedTemplate.name,
                                hash: null,
                                width: clonedTemplate.size.width,
                                height: clonedTemplate.size.height,
                                serialized_template: JSON.stringify(updatedTemplateForm),
                                priority: clonedTemplate.priority,
                                groups: null,
                                thumb: null
                            }

                            return <GridCell key={template.id} onClick={() => this.handleSelectCustomTemplate(clonedTemplate)} title={template.name}>
                                <PreviewTemplateSVG
                                    isAlt={isAlt}
                                    templateGallery={galleryFromTemplate}
                                    randomProduct={randomProduct}
                                />
                            </GridCell>
                        })}
                    </React.Fragment>
                }
            </ContentGrid>
        </div>
    }
}

const ContentGrid = css(Grid)({
    gridGap: '15px',
    display: 'grid',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
        overflow: 'hidden',
        ':hover': {
            background: '#fff',
            cursor: 'pointer',
        },
    },
    ' .size-format': {
        color: Theme.blue,
    },
    ' .template-name': {
        marginBottom: '5px',
        padding: '5px',
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    ' .template-thumbnail': {
        ' img': {
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            border: '1px solid #ddd',
            background: '#cccccc',
        },
    },
})

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        collageLayoutsForm: selectFormValues(state, 'collageLayoutsForm'),
        randomProduct: state.randomProduct,
        sizes: state.sizes,
        userId: state.project.data.user?.id,
        templates: state.templates,
    }
}

export default connect(mapStateToProps, {
    initForm,
    change,
    setGradientsToStore,
    setRandomProduct,
})(TemplateGalleryModal)